import React, { useContext, useState } from 'react';
import { Button } from 'uxd-design-system-components';
import PropTypes from 'prop-types';
import SendToDelegatorData from '~types/api/SendToDelegatorData';
import SendResultsButtonProps from '~types/components/SendResultsButtonProps';
import getConType from '../../utils/getConType/getConType';
import getSource from '../../utils/getSource/getSource';
import AddContactFormModal from '../AddContactFormModal/AddContactFormModal';
import CommentModal from '../CommentModal/CommentModal';
import getText from '../../assets/locale/locale';
import useQuery from '../../utils/useQuery/useQuery';
import addDataLayerTrigger from '../../utils/addDataLayerTrigger/addDataLayerTrigger';
import LocaleContext from '../../contexts/LocaleContext';
import { handleAVIError, sendToDelegator } from '../../api/index';

const SendResultsButton = ({
  setNotificationContent,
  setNotificationOpen,
  sessionId,
  resultsJSON,
  coldContactData,
  setColdContactData,
  userComments,
  setUserComments,
  contactName,
  buttonText,
  faInfo,
  sendResultsButtonDisabled,
  setSendResultsButtonDisabled,
  planningTopics,
  investmentInvolvementCode
}: SendResultsButtonProps) => {
  const locId = useContext(LocaleContext);
  const urlQuery = useQuery();
  const faNo = urlQuery.get('faNo') || faInfo.faNo;
  const faNoParam = urlQuery.get('faNo');
  const entityIdParam = urlQuery.get('EID');
  const conId = urlQuery.get('conId');
  const utmSource = urlQuery.get('utm_source');
  const utmContent = urlQuery.get('utm_content');
  const faFullName = `${faInfo.faFirstName} ${faInfo.faLastName}`;

  const [displayColdContactModal, setDisplayColdContactModal] = useState(false);
  const [displayCommentModal, setDisplayCommentModal] = useState(false);
  const [isSendResultsLoading, setSendResultsLoading] = useState(false);

  const successNotificationContentHandler = () => {
    if (coldContactData) {
      if (faInfo.faFirstName && faInfo.faLastName) {
        return getText(
          locId,
          'coldContactSuccessMessage',
          'resultsPage'
        ).replace('%FA_NAME%', faFullName);
      }
      return getText(locId, 'coldContactSuccessMessage', 'resultsPage').replace(
        '%FA_NAME%',
        getText(locId, 'yourFaDefaultText', 'resultsPage')
      );
    }
    return getText(locId, 'successMessage', 'resultsPage');
  };

  async function emailResultsToFA() {
    setSendResultsLoading(true);
    addDataLayerTrigger('email-fa', { faId: faNo });
    const params = {
      locale: locId,
      notifyFaInd: 'Y',
      storeToDbInd: 'Y',
      archiveInd: coldContactData ? 'N' : 'Y',
      sysId: 'PRP',
      appName: 'MyPriorities',
      conType: getConType(utmSource, utmContent),
      utmSource: getSource(utmSource, entityIdParam, faNoParam, conId),
    };

    const data: SendToDelegatorData = {
      faNo,
      contactNo: conId,
      contactName,
      branchNo: faInfo.branchNo,
      results: resultsJSON,
      sessionId,
      coldContactData,
      userComments,
      utmSource: getSource(utmSource, entityIdParam, faNoParam, conId),
      planningTopics,
      investmentInvolvementCode
    };

    const responseType = 'json';

    sendToDelegator(params, data, responseType)
      .then(() => {
        setSendResultsLoading(false);
        setNotificationContent({
          message: successNotificationContentHandler(),
          type: 'confirmation',
        });
        setNotificationOpen(true);
        setSendResultsButtonDisabled(true);
        addDataLayerTrigger('confirmation-visible', {});
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          handleAVIError(error, locId);
        }
        setSendResultsLoading(false);
        setNotificationContent({
          message: coldContactData
            ? getText(locId, 'coldContactErrorMessage', 'resultsPage').replace(
                '%FA_FIRSTNAME%',
                faInfo.faFirstName
              )
            : getText(locId, 'errorMessage', 'resultsPage'),
          type: 'error',
        });
        setNotificationOpen(true);
        /* eslint-disable no-console */
        console.error(error);
      });
  }

  const modalHandler = () => {
    if (contactName.firstName) {
      setDisplayCommentModal(true);
    } else {
      setDisplayColdContactModal(true);
    }
  };

  return (
    <>
      {sendResultsButtonDisabled ? (
        <Button
          label={getText(locId, 'successButton', 'resultsPage')}
          styleVariant="reversed-secondary"
          disabled
          attributes={{
            value: '',
            id: 'email-FA-button',
            'data-testid': 'email-FA-button',
          }}
          onClick={() => {}}
        />
      ) : (
        <Button
          loading={isSendResultsLoading}
          icon="message-send"
          label={buttonText}
          onClick={() => modalHandler()}
          attributes={{
            value: '',
            id: 'email-FA-button',
            'data-testid': 'email-FA-button',
          }}
        />
      )}
      <AddContactFormModal
        displayColdContactModal={displayColdContactModal}
        setDisplayColdContactModal={setDisplayColdContactModal}
        emailResultsToFA={emailResultsToFA}
        coldContactData={coldContactData}
        setColdContactData={setColdContactData}
        faInfo={faInfo}
      />
      <CommentModal
        displayCommentModal={displayCommentModal}
        setDisplayCommentModal={setDisplayCommentModal}
        emailResultsToFA={emailResultsToFA}
        setUserComments={setUserComments}
        faInfo={faInfo}
        contactName={contactName}
      />
    </>
  );
};

SendResultsButton.propTypes = {
  setNotificationContent: PropTypes.func.isRequired,
  setNotificationOpen: PropTypes.func.isRequired,
  setSendResultsButtonDisabled: PropTypes.func.isRequired,
  sendResultsButtonDisabled: PropTypes.bool.isRequired,
  sessionId: PropTypes.string.isRequired,
  coldContactData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string
  }),
  setColdContactData: PropTypes.func.isRequired,
  contactName: PropTypes.objectOf(PropTypes.string),
  userComments: PropTypes.string.isRequired,
  setUserComments: PropTypes.func.isRequired,
  resultsJSON: PropTypes.objectOf(PropTypes.any).isRequired,
  faInfo: PropTypes.shape({
    faNo: PropTypes.string,
    branchNo: PropTypes.string,
    faFirstName: PropTypes.string,
    faLastName: PropTypes.string,
    faPrimaryEmail: PropTypes.string,
    faTeamEmail: PropTypes.string,
    faPhoto: PropTypes.string
  }).isRequired,
  buttonText: PropTypes.string.isRequired,
  planningTopics: PropTypes.arrayOf(PropTypes.string),
  investmentInvolvementCode: PropTypes.string,
};

SendResultsButton.defaultProps = {
  coldContactData: null,
  contactName: null,
};

export default SendResultsButton;
